import React, { useState, useEffect } from 'react';
import { CHART_SERIES, CHART_REMAINDER, chartSeriesColor } from './styleTokens';
import exportFromJSON from 'export-from-json';
import { Badge, Form } from 'react-bootstrap';
import { BsFillCloudFill, BsCircleFill, BsBraces } from 'react-icons/bs';
import { getDiscoverResultsJson } from './DiscoverResults';
import DataSources from './DataSources'
import RiskScoreMap from './RiskScoreMap';
import { SiPostgresql, SiOracle, SiMicrosoftsqlserver, SiMariadb, SiMongodb, SiMicrosoftazure, SiMicrosoftsharepoint, SiAmazondynamodb } from 'react-icons/si';
import { GrMysql } from 'react-icons/gr';
import S3Icon from 'react-aws-icons/dist/aws/logo/S3';
import RedShiftIcon from 'react-aws-icons/dist/aws/logo/Redshift';
import {
  FaFolder,
  FaFolderOpen,
  FaExchangeAlt,
  FaSnowflake,
  FaLock
} from 'react-icons/fa';
 // Utility functions and components to be shared between components.
import DiscoveryTooltip from 'DiscoveryTooltip';
import { getSourceNomenclature } from './nomenclature';

const LOW_CONFIDENCE_THRESHOLD = 2;

const SecuredIcon = (props) => (
  <DiscoveryTooltip  
     placement="top"
    text={ props.text ? props.text : 'This field has been secured with encryption. Format preserving encryption can still register as sensitive data.'}
  >
  <FaLock  size={15} style={({ marginBottom: '2px', marginRight: '1px'  })}/>
    </DiscoveryTooltip>
  
  )
const flattenObj = (obj, targetKey) => {
  let result = {};

  try {
    for (const prop in obj) {
      result[prop] = obj[prop][targetKey];
    }
  } catch(e) {
    console.log(e);
  }
  return result;
}

const getMonth = (date) => {
  const dateOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  };
  const result = (new Date(date)).toLocaleDateString('en-US', dateOptions);
  return result;
}

const getTime = (date) => {
  const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
  const result = (new Date(date)).toLocaleString('en-US', timeOptions);
  return result;
}

const truncateCenter = (fullStr, strLen, separator) => {
  if (fullStr) {
    if (fullStr.length <= strLen) return fullStr;

    separator = separator || '...';
  
    let sepLen = separator.length,
      charsToShow = strLen - sepLen,
      frontChars = Math.ceil(charsToShow/2),
      backChars = Math.floor(charsToShow/2);
  
    return fullStr.substr(0, frontChars) +
      separator +
      fullStr.substr(fullStr.length - backChars);
  }
};

const commafy = (num) => String(num).replace(/(.)(?=(\d{3})+$)/g,'$1,');

const getDisplayDate = (date) => {
  const dateOptions = { 
    // weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  };
  const displayDate = (new Date(date)).toLocaleDateString('en-US', dateOptions);
  return displayDate;
}

/**
 * Compact ledger date — "Sep 29 26": short month, day, 2-digit year, no comma. The landscape
 * ledgers put the date in their FIRST column, where the long form ("September 29, 2026 2:31 PM")
 * eats the width the data-source name needs. Call sites keep the full stamp in a title tooltip.
 */
const getCompactDate = (date) => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }).replace(',', '');
};

const getDisplayDateNumeric = (date) => {
  const dateOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  };
  const result = (new Date(date)).toLocaleDateString('en-US', dateOptions);
  return result;
}

const getDisplayTime = (date) => {
  const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
  const displayTime = (new Date(date)).toLocaleString('en-US', timeOptions);
  return displayTime;
}

const transform = (tablesMeta, sampleList, rowColMap, search) => {
  const tables = {};
  const columnNumToNameMap = {};
  const overview = {};
  const entityTableMap  = {};
  let targetType = null;
  let dbname = null;
  let engine = null;
  let connectionName = null;

  tablesMeta = tablesMeta ? tablesMeta : [];

  for (let tableMeta of tablesMeta) {
    let tableName = tableMeta.table
    tables[tableName] = [];
    columnNumToNameMap[tableName] = [];

    if (tableMeta && tableMeta.target_type) {
      targetType = tableMeta.target_type;
    }

    if (tableMeta && tableMeta.dbname) {
      dbname = tableMeta.dbname;
    }

    if (tableMeta && tableMeta.engine) {
      engine = tableMeta.engine;
    }

    if (tableMeta && tableMeta.connection_name) {
      connectionName = tableMeta.connection_name
    }
    for (let i = 0; i < tableMeta.columns.length; i++) {
      try {
        let column = tableMeta.columns[i];
        let field = column.Field;
        columnNumToNameMap[tableName][i] = field;

        tables[tableName][field] = {
          type: column.Type,
          isNullable: column.IsNullable,
          typeSize: column.TypeSize,
          entities: {}
        };

        if (column.Constraints) {
          tables[tableName][field].constraints = column.Constraints;
        }
      } catch(e) {
        console.log(e);
      }
    }
  }

  rowColMap = rowColMap ? rowColMap : {};
  sampleList = sampleList ? sampleList : [];

  for (let sample of sampleList) {
    if (sample.type === "classification") {
      try {
        let entity = sample.entities[0]

        if (tables[sample.table]['top'] === undefined) {
          tables[sample.table]['top'] = {
            type: 'Entire Table',
            entities: {}
          }
        }

        if (tables[sample.table]['top'].entities[entity.type] === undefined) {
          tables[sample.table]['top'].entities[entity.type] = 1
        } else {
          tables[sample.table]['top'].entities[entity.type]++
        }
      } catch(e) {
        console.log(e);
      }
    } else {
      for (let entity of sample.entities) {
        try {
          if (columnNumToNameMap[sample.table] && (entity.Column >  columnNumToNameMap[sample.table].length)) {
            entity.Column = columnNumToNameMap[sample.table].length - 1
          }
          let column = columnNumToNameMap[sample.table][entity.Column];
          let sampleNumber = sample.sample_number;
          let rowColTable = `${sampleNumber} ${entity.Column} ${sample.offset} ${column} ${sample.table} ${entity.type}`;

          if (!rowColMap.hasOwnProperty(rowColTable)) {
            rowColMap[rowColTable] = {
              count: 1,
              entities: [entity],
              samples: [sample]
            };
          } else {
            rowColMap[rowColTable].count++;
            rowColMap[rowColTable].entities.push(entity);
            rowColMap[rowColTable].samples.push(sample);
          }
        } catch(e) {
          console.log(e);
        }
      }
    }
  }

  const keys = Object.keys(rowColMap);

  for (let i = 0; i < keys.length; i++) {
    try {
      let arr = keys[i].split(' ');
      let table = arr[4];
      let entityType = arr.length > 6 ? arr.splice(5, arr.length - 1).join(' ') : arr[5];
      let column = arr[3];

      if (
        tables &&
        tables[table] &&
        tables[table][column] &&
        tables[table][column].entities
      ) {
        let tableEntities = tables[table][column].entities;
        tableEntities[entityType] = tableEntities[entityType] + 1 || 1;
      }

      overview[entityType] = overview[entityType] + 1 || 1;
      entityTableMap[entityType] = entityTableMap[entityType] || {};
      entityTableMap[entityType][table] = entityTableMap[entityType][table] + 1 || 1;
    } catch(e) {
      console.log(e);
    }
  }

  rowColMap.searchid = search;
  return [tables, overview, entityTableMap, columnNumToNameMap, rowColMap, targetType, dbname, engine, connectionName];
}


const getReportData = (transformedEsData) => {
  let result = {
    searchidValue: transformedEsData.searchid,
    displayidValue: transformedEsData.displayid,
    timestampValue: transformedEsData.timestamp,
    engineValue: transformedEsData.engine,
    databaseValue: transformedEsData.database,
    schemaValue: transformedEsData.schema,
    connectionValue: transformedEsData.connection,
    sampleRulesValue: transformedEsData.sampleRulesValue
  };

  return result;
}

const transformEsData = (metaData, entityData) => {
  let result = {};
  let dbName = '';
  let dbSchema = '';
  let date;
  let tables = null;
  let searchid = '';
  let displayid = '';
  let connection = '';
  let engine = '';
  let sampleRules = '';

  // console.log('transformEsData 1', metaData, entityData);

  try {
    const dateValue = getFirstKeyValue(metaData, 'time_stamp');
    const rulesObj = getFirstKeyValue(metaData, 'rules');
    const dateObj = dateValue && (new Date(dateValue));
    date = dateObj && dateObj.getTime();
    sampleRules = rulesObj.name;
    searchid = getFirstKeyValue(metaData, 'id');
    displayid = getDisplayId(searchid);
    dbName = getFirstKeyValue(metaData, 'dbname');
    dbSchema = getFirstKeyValue(metaData, 'dbschema');
    connection = getFirstKeyValue(metaData, 'connection_name');
    engine = getFirstKeyValue(metaData, 'engine');
    const tableMetaMap = getTableMetaMap(metaData);

    if (entityData) {
      const tablesToEntitiesMap = getTablesToEntitiesMap(entityData);
      const tablesToSensitiveColumns = mapColumnsToEntities(tableMetaMap, tablesToEntitiesMap);
      // console.log('transformEsData 2', tablesToSensitiveColumns);
      tables = transformTablesToEntityData(tablesToSensitiveColumns);
      // console.log('transformEsData 3', tables);
    }

    result = {
      searchid: searchid,
      displayid: displayid,
      database: dbName,
      schema: dbSchema || dbName,
      timestamp: date,
      connection: connection,
      engine: engine,
      sampleRulesValue: sampleRules,
      ...tables && ({ tables: tables })
    }
  } catch(e) {
    console.log(e);
  }
  return result;
}

const transformExportData = (metaData, entityData) => {
  let result = {};
  let dbName = '';
  let dbSchema = '';
  let date;
  let tables = null;
  let searchid = '';
  let displayid = '';
  let connection = '';
  let engine = '';
  let sampleRules = '';

  // console.log('transformExportData 1', metaData, entityData);

  try {
    const dateValue = getFirstKeyValue(metaData, 'time_stamp');
    const rulesObj = getFirstKeyValue(metaData, 'rules');
    const dateObj = dateValue && (new Date(dateValue));
    date = dateObj && dateObj.getTime();
    sampleRules = rulesObj.name;
    searchid = getFirstKeyValue(metaData, 'id');
    displayid = getDisplayId(searchid);
    dbName = getFirstKeyValue(metaData, 'dbname');
    dbSchema = getFirstKeyValue(metaData, 'dbschema');
    connection = getFirstKeyValue(metaData, 'connection_name');
    engine = getFirstKeyValue(metaData, 'engine');
    const tableMetaMap = getTableMetaMap(metaData);

    if (entityData) {
      const tablesToEntitiesMap = getTablesToEntitiesMap(entityData);
      // console.log('transformExportData 2', tablesToEntitiesMap)
      const tablesToSensitiveColumns = mapColumnsToEntities(tableMetaMap, tablesToEntitiesMap);
      // console.log('transformExportData 3', tablesToSensitiveColumns);
      tables = transformTablesToEntityData(tablesToSensitiveColumns);
      // console.log('transformExportData 4', tables);
    }

    result = {
      searchid: searchid,
      displayid: displayid,
      database: dbName,
      schema: dbSchema || dbName,
      timestamp: date,
      connection: connection,
      engine: engine,
      sampleRulesValue: sampleRules,
      ...tables && ({ tables: tables })
    }
  } catch(e) {
    console.log(e);
  }
  return result;
}

const getEntityTablesValue = (esResults) => {
  let result = {};

  try {
    const schemas = esResults.schemas || [];
    schemas.forEach(schema => {
      const tables = schema.tables || [];
      tables.forEach(table => {
        const tableName = table.name;
        const columns = table.columns || [];
        // console.log('getEntityTablesValue table', table);
        columns.forEach(column => {
          const entities = column.found_entities || {};
          for (let entity in entities) {
            if (entity in result) {
              result[entity][tableName] = result[entity][tableName] + entities[entity] || entities[entity];
            } else {
              result[entity] = {[tableName]: entities[entity]};
            }
          }
        });
      });
    });
  } catch(e) {
    console.log(e);
  }

  return result;
}

const getEntityTables = (tableEntities, entityBuckets, tableSampledRowCountsMap) => {
  let result = { 'LOW_INCIDENCE_FINDINGS': { 'TOTAL': {} } };

  // Calculate totals
  try {
    const aggs = tableEntities.aggregations || {};
    const tableBuckets = aggs.tables && aggs.tables.buckets || [];

    tableBuckets.forEach((bucket) => {
      const table = bucket && bucket.key || '';
      const totalSensitiveEntitiesAgg = bucket.nested_aggs && bucket.nested_aggs.total_sensitive_entities;
      const totalSensitiveEntitiesValue = totalSensitiveEntitiesAgg && totalSensitiveEntitiesAgg.value || 0;
      result['TOTAL'] = {
        ...result['TOTAL'],
        [table]: totalSensitiveEntitiesValue
      }
    });
  } catch(e) {
    console.log(e);
  }

  try {
    const aggs = tableEntities.aggregations || {};
    const tableBuckets = aggs.tables && aggs.tables.buckets || [];

    tableBuckets.forEach((bucket) => {
      let table = bucket.key;
      let sampledTable =  tableSampledRowCountsMap[table] ?  tableSampledRowCountsMap[table] : {};

      let sampledRow =   sampledTable.table_sampled_row_qty ? sampledTable.table_sampled_row_qty : 0;
      const sampledRowCount = sampledRow ? sampledRow : 0;

      const fields = bucket.nested_aggs && bucket.nested_aggs.fields && 
        bucket.nested_aggs.fields.buckets || []
      ;
      fields.forEach(fb => {
        const entities = fb.entities.buckets;
        entities.forEach(eb => {
          const entity = eb.key;
          const count = eb.count.value;
          const percent = Math.floor((count / sampledRowCount) * 100);
          // const percent = Number((count / sampledRowCount) * 100).toFixed(1);

          if (percent <= LOW_CONFIDENCE_THRESHOLD) {
            result['LOW_INCIDENCE_FINDINGS'][entity] = {
              ...result['LOW_INCIDENCE_FINDINGS'][entity] && ({ ...result['LOW_INCIDENCE_FINDINGS'][entity] }),
              [table]: (result['LOW_INCIDENCE_FINDINGS'][entity] && result['LOW_INCIDENCE_FINDINGS'][entity][table]) ? 
                (result['LOW_INCIDENCE_FINDINGS'][entity][table] + count) : (count)
            }

            result['LOW_INCIDENCE_FINDINGS']['TOTAL'][table] = result['LOW_INCIDENCE_FINDINGS']['TOTAL'][table] ?
              result['LOW_INCIDENCE_FINDINGS']['TOTAL'][table] + count :
              count
            ;
          } else {
            result[entity] = {
              ...result[entity] && ({ ...result[entity] }),
              [table]: (result[entity] && result[entity][table]) ? (result[entity][table] + count) : count
            }
          }
        });
      });
    });
  } catch(e) {
    console.log(e);
  }
  // console.log('getEntityTables result', result);
  return result;
}

// const getEntityTables = (tableEntities, entityBuckets) => {
//   console.log('getEntityTables params', tableEntities, entityBuckets);
//   let result = {};

//   try {
//     const aggs = tableEntities.aggregations || {};
//     const tableBuckets = aggs.tables && aggs.tables.buckets || [];

//     tableBuckets.forEach((bucket) => {
//       const table = bucket && bucket.key || '';
//       const totalSensitiveEntitiesAgg = bucket.nested_aggs && bucket.nested_aggs.total_sensitive_entities;
//       const totalSensitiveEntitiesValue = totalSensitiveEntitiesAgg && totalSensitiveEntitiesAgg.value || 0;
//       result['TOTAL'] = {
//         ...result['TOTAL'],
//         [table]: totalSensitiveEntitiesValue
//       }
//     });
//   } catch(e) {
//     console.log(e);
//   }

//   try {
//     const aggs = entityBuckets.aggregations || {};
//     const tableBuckets = aggs.tables && aggs.tables.buckets || [];

//     tableBuckets.forEach((bucket) => {
//       let table = bucket.key;

//       const entities = bucket.nested_aggs && bucket.nested_aggs.entities && 
//         bucket.nested_aggs.entities.buckets || []
//       ;

//       entities.forEach(entity => {
//         const type = entity.key;
//         const count = entity.count && entity.count.value || 0;

//         result[type] = {
//           ...result[type],
//           [table]: count
//         }
//       });
//     });
//   } catch(e) {
//     console.log(e);
//   }
//   console.log('getEntityTables result', result);
//   return result;
// }

const getFoundEntitiesValue = (esResults) => {
  let result = {};

  try {
    const schemas = esResults.schemas || [];
    schemas.forEach(schema => {
      const tables = schema.tables || [];
      tables.forEach(tableObj => {
        const tableName = tableObj.name;
        result[tableName] = {};
        const table = result[tableName];
        const columns = tableObj.columns || [];

        columns.forEach(col => {
          const column = col.column_name;
          const entities = col.found_entities || {};
          const type = col.data_type;
          table[column] = {
            entities: entities,
            type: type
          }
        });
      });
    });
  } catch(e) {
    console.log(e);
  }

  return result;
}

const getMetaValue = (esResults) => {
  let result = {};

  try {
    const schemas = esResults.schemas || [];
    schemas.forEach(schema => {
      const tables = schema.tables || [];
      tables.forEach(table => {
        const tableName = table.name;
        const columnArr = table.columns || [];
        const columns = columnArr.map(col => col.column_name);
        result[tableName] = columns;
      });
    });
  } catch(e) {
    console.log(e);
  }

  return result;
}

const getOverviewValue = (esResults) => {
  let result = {};

  try {
    const schemas = esResults.schemas || [];
    schemas.forEach(schema => {
      const tables = schema.tables || [];
      tables.forEach(table => {
        const columns = table.columns || [];
        columns.forEach(col => {
          const entities = col.found_entities || {};
          for (let entity in entities) {
            result[entity] = result[entity] + entities[entity] || entities[entity];
          }
        })
      });
    });
  } catch(e) {
    console.log(e);
  }

  return result;
}

const getDisplayId = (searchid) => {
  let displayid = searchid;

  if (searchid) {
    try {
      const dateIndex = searchid.lastIndexOf('_');
      const dateStr = searchid.slice(dateIndex + 1, searchid.length);
      const dateInt = parseInt(dateStr);
      const date = new Date(dateInt);
      const monthDate = date.getMonth() + 1;
      const hour = date.getHours();
      const minute = date.getMinutes();
  
      displayid = searchid.slice(0, dateIndex);
      displayid += ' ' + monthDate;
      displayid += '.' + date.getDate();
      displayid += '.' + date.getFullYear().toString().slice(-2);
      displayid += '.' + hour;
      displayid += minute;
    } catch(e) {
      console.log(e);
    }
  }
  
  return displayid;
}

const transformTablesToEntityData = (tablesToSensitiveColumns) => {
  let result = [];

  try {
    for (let table in tablesToSensitiveColumns) {
      const item = {
        name: table,
        columns: tablesToSensitiveColumns[table]
      };
      result.push(item);
    }
  } catch(e) {
    console.log(e);
  }

  return result;
}

const transformEntityBuckets = (entityBuckets) => {
  const result = {};

  try {
    entityBuckets.forEach(entityItem => {
      const entity = entityItem.key;
      const count = entityItem.count && entityItem.count.value || 0;
      result[entity] = count;
    })
  } catch(e) {
    console.log(e);
  }

  return result;
}

const mapColumnsToEntities = (tableMetaMap, tablesToEntitiesMap) => {
  // Deep clone tableMetaMap
  const result = JSON.parse(JSON.stringify(tableMetaMap));

  try {
    for (let table in tablesToEntitiesMap) {
      const sensitiveColumns = tablesToEntitiesMap[table];
      sensitiveColumns.forEach(col => {
        const entities = col.entities && col.entities.buckets || [];
        const tableMetaColumns = result[table];
        const columnNum = col.key;
        if (tableMetaColumns[columnNum]) {
          tableMetaColumns[columnNum].found_entities = transformEntityBuckets(entities);
        }
      });
    }
  } catch(e) {
    console.log(e);
  }

  return result;
}

const getTablesToEntitiesMap = (entityAggs) => {
  const result = {};

  try {
    const aggregations = entityAggs.aggregations || {};
    // const tableBuckets = aggregations.sampled_tables && 
    //   aggregations.sampled_tables.tables && 
    //   aggregations.sampled_tables.tables.buckets || []
    
    //   ;
    const tableBuckets = aggregations.tables &&
      aggregations.tables.buckets || [];
    tableBuckets.forEach(bucket => {
      const table = bucket.key || '';

      const sensitiveColumns = bucket.sensitive_columns 
        && bucket.sensitive_columns.sensitive_columns 
        && bucket.sensitive_columns.sensitive_columns.buckets || []
      ;

      result[table] = sensitiveColumns;
    });
  } catch(e) {
    console.log('getTablesToEntitiesMap error', e);
  }
  return result;
}

const getTableMetaMap = (metaData) => {
  let result = {};

  try {
    metaData.forEach((tableHit) => {
      const table = tableHit.table;
      const columnHits = tableHit.columns || [];
      const columns = getColumnList(columnHits);
      result[table] = columns;
    });
  } catch(e) {
    console.log(e);
  }

  return result;
}

const getColumnList = (columnHits) => {
  let result = [];

  try {
    const columns = columnHits.map((col) => {
      const item = {
        column_name: col.Field,
        data_type: col.Type,
        found_entities: {}
      }
      return item;
    });
    if (columns) result = columns;
  } catch(e) {
    console.log(e);
  }

  return result;
}

const getFirstKeyValue = (arr, key) => {
  let result;

  try {
    arr.forEach(obj => {
      if (key in obj) {
        result = obj[key];
        return result;
      }
    })
  } catch(e) {
    console.log(e);
  }

  return result || '';
}

// Stable color palette keyed by registered_service_id — used by DataSources table chips,
// the risk-profile stacked bar chart, and any other surface that needs per-source coloring.
// Native C2 rows (registered_service_id == null) get the neutral C2 brand color.
const _SERVICE_COLOR_CACHE = {};
const _C2_NATIVE_COLOR = '#315DFF';
const _SERVICE_PALETTE_SIZE = 8;

const getSourceColor = (svcId) => {
  if (!svcId) return _C2_NATIVE_COLOR;
  if (_SERVICE_COLOR_CACHE[svcId]) return _SERVICE_COLOR_CACHE[svcId];
  // Ensure a cached palette has at least SERVICE_PALETTE_SIZE entries; assign next slot.
  if (!_SERVICE_COLOR_CACHE._palette) {
    _SERVICE_COLOR_CACHE._palette = CHART_SERIES.slice(0, _SERVICE_PALETTE_SIZE);
    _SERVICE_COLOR_CACHE._nextSlot = 0;
  }
  const slot = _SERVICE_COLOR_CACHE._nextSlot % _SERVICE_PALETTE_SIZE;
  _SERVICE_COLOR_CACHE._nextSlot += 1;
  const color = _SERVICE_COLOR_CACHE._palette[slot];
  _SERVICE_COLOR_CACHE[svcId] = color;
  return color;
};

const getSourceLabel = (svcId) => {
  if (!svcId) return 'C2';
  // Kebab-case id → human-friendly label. LAST-RESORT fallback only — prefer the
  // connection's `registeredServiceName` (the integration's display name, resolved
  // server-side) wherever it's available; instance ids are admin-chosen free text
  // ("axis-delphix-2026") so no string heuristic can recover the vendor from them.
  // Legacy fixed ids keep their old mapping:
  //   "integrations-delphix" → "Delphix"; "discover-default" → "Discover".
  // Anything else title-cases every segment: "axis-delphix-2026" → "Axis Delphix 2026".
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const parts = svcId.split('-').filter(Boolean);
  if (parts.length === 0) return 'C2';
  if (parts[0] === 'integrations' && parts.length > 1) return cap(parts[parts.length - 1]);
  if (parts.length === 2 && parts[1] === 'default') return cap(parts[0]);
  return parts.map(cap).join(' ');
};

const getPieVisData = (overview) => {
  let result = {};

  // const colorGradient = new Gradient();
  // colorGradient.setGradient('#003f5c', '#7a5195', '#ef5675', '#ffa600');

  let total = 0;
  let otherCount = 0;
  let colorCount = 3;
  let overviewArr = [];
  let newData = [[], []];
  let newColors = [];

  if (overview) {
    overviewArr = Object.keys(overview);
    // const overviewData = [];
    overviewArr.forEach(e => {
      if (e !== 'LOW_INCIDENCE_FINDINGS') {
        let val = {
          theta: overview[e],
          type: e,
          title: e,
          radius: 140,
          innerRadius: 100
        };
        total += overview[e];
        newData[0].push(val);
        colorCount++;
      }
    });

    newData[0].sort((a, b) => b.theta - a.theta);

    if (overview['LOW_INCIDENCE_FINDINGS']) {
      for (const e in overview['LOW_INCIDENCE_FINDINGS']) {
        const item = {
          theta: overview['LOW_INCIDENCE_FINDINGS'][e],
          type: e,
          title: e,
          radius: 140,
          innerRadius: 100
        };
        newData[1].push(item);
        total += overview['LOW_INCIDENCE_FINDINGS'][e];
        otherCount += item.theta;
      }
    }
  
    if (newData[1].length > 1) {
      let otherVal = {
        theta: otherCount,
        type: 'Low Incidence Findings',
        title: 'Low Incidence Findings',
        radius: 140,
        innerRadius: 100,
        color: CHART_REMAINDER
      };
      newData[0].push(otherVal);
  
      // const otherGradient = new Gradient();
      // otherGradient.setGradient('#003f5c', '#7a5195', '#ef5675', '#ffa600');
      // otherGradient.setMidpoint(newData[1].length);
      // const otherColors = otherGradient.getArray();
      const otherColors = newData[1].map((_, i) => chartSeriesColor(i));
  
      for (let i = 0; i < newData[1].length; i++) {
        newData[1][i].color = otherColors[i];
      }
    }
    // colorGradient.setMidpoint(colorCount);
    // newColors = colorGradient.getArray();
    newColors = Array.from({ length: colorCount }, (_, i) => chartSeriesColor(i));
    // console.log('newColors', newColors);
  
    for (let i = 0; i < newData[0].length; i++) {
      if (newData[0][i].type !== 'Low Incidence Findings') {
        newData[0][i].color = newColors[i];
      }
    }
    // console.log('newData', newData);
  }

  result = { otherCount, newData, total, newColors };
  return result;
}

// const getPieVisData = (overview) => {
//   let result = {};

//   const colorGradient = new Gradient();
//   colorGradient.setGradient('#003f5c', '#7a5195', '#ef5675', '#ffa600');

//   let total = 0;
//   let otherCount = 0;
//   let colorCount = 3;
//   let overviewArr = [];
//   let newData = [[], []];
//   let newColors = [];

//   if (overview) {
//     overviewArr = Object.keys(overview);
//     let overviewData = overviewArr.map((key) => {
//       let val = {
//         theta: overview[key],
//         type: key,
//         title: key,
//         radius: 140,
//         innerRadius: 100
//       };
//       total += overview[key];
  
//       return val;
//     })
//     .sort((a, b) => b.theta - a.theta);
  
//     for (let i = 0; i < overviewData.length; i++) {
//       let percent = Math.floor((overviewData[i].theta / total) * 100);
//       overviewData[i].percent = percent;
  
//       if (percent >= 3) {
//         newData[0].push(overviewData[i]);
//         colorCount++;
//       } else {
//         newData[1].push(overviewData[i]);
//         otherCount += overviewData[i].theta;
//       }
//     }
  
//     if (newData[1].length > 1) {
//       let otherVal = {
//         theta: otherCount,
//         type: 'Low Incidence Findings',
//         title: 'Low Incidence Findings',
//         radius: 140,
//         innerRadius: 100,
//         color: '#ffa600'
//       };
//       newData[0].push(otherVal);
  
//       const otherGradient = new Gradient();
//       otherGradient.setGradient('#003f5c', '#7a5195', '#ef5675', '#ffa600');
//       //otherGradient.setGradient('#8856a7', '#9ebcda', '#e0ecf4');
//       otherGradient.setMidpoint(newData[1].length);
//       const otherColors = otherGradient.getArray();
  
//       for (let i = 0; i < newData[1].length; i++) {
//         newData[1][i].color = otherColors[i];
//       }
//     }
  
//     colorGradient.setMidpoint(colorCount);
//     newColors = colorGradient.getArray();
  
//     for (let i = 0; i < newData[0].length; i++) {
//       if (newData[0][i].percent >= 3) {
//         newData[0][i].color = newColors[i];
//       }
//     }
//   }

//   result = { otherCount, newData, total, newColors };

//   return result;
// }

const exportData = (esResults) => {
  try {
    const fileName = 'export';
    const exportType = exportFromJSON.types.json;
    exportFromJSON({ data: esResults, fileName, exportType });
  } catch(e) {
    // If file too big for formatted JSON..
    try {
      const fileName = 'export';
      const extension = 'json';
      exportFromJSON({ data: JSON.stringify(esResults), fileName, extension });
    } catch(e) {
      console.log(e);
      throw e;
    }
  }
}

const getExportData = async (esTableData, foundEntities, searchid) => {
  try {
    let dbData = esTableData[0];
    let entityTables = [];

    for (let entity in foundEntities) {
      let tableObj = {
        name: entity,
        columns: []
      }

      for (let col in foundEntities[entity]) {
        let entityLength = (Object.keys(foundEntities[entity][col].entities)).length;
        if (entityLength > 0) {
          let colObj = {
            column_name: col,
            found_entities: foundEntities[entity][col].entities
          }
          tableObj.columns.push(colObj);
        }
      }
      entityTables.push(tableObj);
    }

    let data = {
      search_set: {
        set_name: searchid,
        database: {
          name: dbData.dbname,
          schemas: [
            {
              name: dbData.dbschema,
              tables: entityTables
            }
          ]
        }
      }
    };

    return data;
  } catch(e) {
    console.log(e);
  }
}

const transformOverviewData = (overviewData) => {
  const result = {};

  try {
    const buckets = overviewData.aggregations.overview.entities.buckets;
    buckets.forEach((bucket) => {
      const type = bucket.key;
      const count = bucket.count && bucket.count.value;
      result[type] = count;
    })
  } catch(e) {
    console.log(e);
  }

  return result;
}

/* insert react-vis data, get a 3 column legend..
 * array items
 * {
 *   color: 'red',
 *   theta: 54,
 *   type: 'PDF'
 * }
*/

const PieChartLegend = (props) => {
  const [columns, setColumns] = useState([]);
  const { data } = props;
  // const columnWidth = 3;
  const columnWidth = props.columnWidth
  const columnHeight = Math.ceil(data.length / columnWidth);

  useEffect(() => {
    let columnsValue = [];
    let iterator = 0;

    for (let i = 0; i < columnWidth; i++) {
      let stack = [];

      for (let j = 0; j < columnHeight; j++) {
        if (data[iterator]) {
          stack.push(data[iterator]);
          iterator++;
        }
      }
      columnsValue.push(stack);
    }
    setColumns(columnsValue);
  }, [data]);

  return (
    <div className='text-left mt-3'>
      <h5>
        { props.legendHeader }
        <Badge
          style={{ fontWeight: '500' }}
          className="ml-2 p-2 discovery-badge"
          variant="primary"
        >
          {data.length} total
        </Badge>
      </h5>
      <div className='d-flex'>
        { columns.map((col, i) => (
            <div key={i} className='w-100'>
              { col.map((item, j) => (
                  <div key ={j} style={{ fontSize: '12px' }} className='mb-3 mt-3'>
                    <BsCircleFill className='mr-2' color={item.color} />
                    { truncateCenter(item[props.legendLabelProperty], 16, '..') } ({ item[props.legendValueProperty] } { props.legendValueSuffix && props.legendValueSuffix })
                  </div>
                )
              )}
            </div>
          )
        )}
      </div>
    </div>
  )
}



// get risk score ( on scale of 1 to 6 ) from array of findings
const getRiskScore = (findings, medicalEntityCount = 0) => {
  let riskScore = 1;
  const entities = findings.map((finding) => finding && finding.entity) || [];

  entities.forEach((entity) => {
    if (RiskScoreMap[entity]) {
      riskScore = Math.max(RiskScoreMap[entity], riskScore);
    }
  });

  // If medical terms are found and count > 1000, set to PHI risk level
  if (medicalEntityCount > 1000) {
    riskScore = 6;
  }

  return riskScore;
}

const RISK_LEVELS = {
  1: {
    label: 'NONE',
    color: '#dbe2e8',
    description: <span>No risk found.</span>,
    categories: []
  },
  2: {
    label: 'LOW',
    color: '#00b050',
    description: <span>Found <span className='accent'>CONTACT</span> data.</span>,
    categories: ['CONTACTS']
  },
  3: {
    label: 'MEDIUM',
    color: '#ffb800',
    textColor: '#000',
    description: <span>Found <span className='accent'>CONTACT</span> and <span className='accent'>UNIQUE IDENTIFIER</span> data.</span>,
    categories: ['CONTACTS', 'UNIQUE IDENTIFIERS']
  },
  4: {
    label: 'MEDIUM HIGH',
    color: '#ff7300',
    description: <span>Found <span className='accent'>CONTACT</span>,<span className='accent'>UNIQUE IDENTIFIER</span> and <span className='accent'>FINANCIAL</span> data.</span>,
    categories: ['CONTACTS', 'UNIQUE IDENTIFIERS', 'FINANCIAL']
  },
  5: {
    label: 'VERY HIGH',
    color: '#ff0000',
    description: <span>Found <span className='accent'>CONTACT</span>,<span className='accent'>UNIQUE IDENTIFIER</span>, <span className='accent'>FINANCIAL</span> and <span className='accent'>HEALTHCARE</span> data.</span>,
    categories: ['CONTACTS', 'UNIQUE IDENTIFIERS', 'FINANCIAL', 'HEALTHCARE']
  },
  6: {
    label: 'CRITICAL',
    color: '#8B0000',
    description: <span>Found <span className='accent'>PHI</span> data with medical context.</span>,
    categories: ['PHI']
  }
}

const getGlueTargetData = (connection, rules) => {
  if (!connection || !rules) return null;
  let result = {};
  const type = connection.type;

  try {
    let targetType = 'JDBC';
    let s3Url = null;
    let warehouse = null;

    switch (type) {
      case 'mongodb':
        targetType = 'MONGODB';
        break;
      case 'snowflake':
        targetType = 'SNOWFLAKE';
        warehouse = connection.warehouse;
        break;
      case 's3':
        targetType = 'S3';
        s3Url = connection.host;
        break;
      case 'sftp':
        targetType = 'SFTP';
        break;
      default:
        targetType = 'JDBC';
        break;
    }

    result = {
      connectionName: connection.name,
      target: {
        type: targetType,
        connection_name: connection.name,
        ...s3Url && ({ s3_url: connection.host }),
        ...warehouse && ({ warehouse: warehouse })
      },
      ...connection.database && ({ db_name: connection.database }),
      engine: type,
      sampleRules: rules,
      sampleStrategy: 'row'
    }
  } catch(e) {
    console.log(e);
  }

  return result;
}

// Normalize AWS RDS engine values to connection type values
const normalizeEngineToConnectionType = (engine) => {
  if (!engine) return engine;
  
  const engineToTypeMap = {
    'postgres': 'postgresql',
    'aurora-postgresql': 'postgresql',
    'oracle': 'oracledb',
    'oracle-ee': 'oracledb',
    'sqlserver': 'mssql',
    'mongo': 'mongodb',
    'mongodb': 'mongodb',
    'dynamo': 'dynamodb',
    'dynamodb': 'dynamodb'
  };
  
  return engineToTypeMap[engine] || engine;
};

// Engine identifiers are case-insensitive at the comparison point: maps below are keyed
// UPPERCASE and lookups uppercase the input. Engine strings can arrive from any upstream
// (integrations workers, manager, manual entry) in either case.
const engineKey = (e) => (e == null ? '' : String(e).toUpperCase());

const engineLabelMap = {
  'AURORA-POSTGRESQL': 'PostgreSQL',
  'AURORA-MYSQL': 'MySQL (Aurora)',
  'POSTGRES': 'PostgreSQL',
  'POSTGRESQL': 'PostgreSQL',
  'MYSQL': 'MySQL',
  'ORACLE': 'OracleDB',
  'ORACLEDB': 'OracleDB',
  'ORACLE-EE': 'OracleDB',
  'MSSQL': 'SQL Server',
  'SQLSERVER': 'SQL Server',
  'REDSHIFT': 'Amazon Redshift',
  'MARIADB': 'MariaDB',
  'S3': 'Amazon S3 Files',
  'SNOWFLAKE': 'Snowflake',
  'MONGODB': 'MongoDB',
  'DYNAMODB': 'DynamoDB',
  'JDBC': 'JDBC',
  'EC2': 'Instance',
  'AZURE_VM': 'Azure VM',
  'GCE_VM': 'GCE VM',
  'AZURE_SQL': 'Azure SQL',
  'GCP_SQL': 'Cloud SQL',
  'AZURE_BLOB': 'Azure Blob Files',
  'GCS': 'Cloud Storage',
  'STREAM': 'Streaming',
  'SFTP': 'SFTP Files',
  'LOCAL': 'Local Files',
  'SHAREPOINT': 'SharePoint Files',
};

const enginePortMap = {
  'AURORA-POSTGRESQL': '5432',
  'POSTGRES': '5432',
  'POSTGRESQL': '5432',
  'MYSQL': '3306',
  'ORACLE': '1521',
  'ORACLEDB': '1521',
  'ORACLE-EE': '1521',
  'MSSQL': '1433',
  'SQLSERVER': '1433',
  'REDSHIFT': '5439',
  'MARIADB': '3306',
  'S3': '443',
  'SNOWFLAKE': '443',
  'MONGODB': '27017',
  'JDBC': 'JDBC',
  'EC2': 'Instance',
  'STREAM': 'Streaming',
  'SFTP': 'SFTP',
  'SHAREPOINT': '443'
};

// Case-insensitive lookup proxies — keep the existing `engineLabels[x]` / `enginePorts[x]`
// call shape working while normalizing the key. Use `engineKey(x)` for explicit lookups.
const engineLabels = new Proxy(engineLabelMap, {
  get: (target, prop) => target[engineKey(prop)],
  has: (target, prop) => engineKey(prop) in target,
});

const enginePorts = new Proxy(enginePortMap, {
  get: (target, prop) => target[engineKey(prop)],
  has: (target, prop) => engineKey(prop) in target,
});

const EngineIcon = (props) => {
  const engineIcons = {
    'POSTGRES': <SiPostgresql className={props.classes} size={props.size} color='#007bc6' />,
    'POSTGRESQL': <SiPostgresql className={props.classes} size={props.size} color='#007bc6' />,
    'AURORA-POSTGRESQL': <SiPostgresql className={props.classes} size={props.size} color='#007bc6' />,
    'MYSQL': <GrMysql className={props.classes} size={props.size} color='#004d5c' />,
    'ORACLE': <SiOracle className={props.classes} size={props.size} color='#ff5151' />,
    'ORACLEDB': <SiOracle className={props.classes} size={props.size} color='#ff5151' />,
    'ORACLE-EE': <SiOracle className={props.classes} size={props.size} color='#ff5151' />,
    'MSSQL': <SiMicrosoftsqlserver className={props.classes} size={props.size} color='#ff6d6d' />,
    'SQLSERVER': <SiMicrosoftsqlserver className={props.classes} size={props.size} color='#ff6d6d' />,
    'REDSHIFT': <RedShiftIcon className={props.classes} size={props.size} color='#ff866b' />,
    'MARIADB': <SiMariadb className={props.classes} size={props.size} color='#1D2E5B' />,
    'S3': <S3Icon className={props.classes} size={props.size} color='#EFBD39' />,
    'SNOWFLAKE': <FaSnowflake className={props.classes} size={props.size} color='#83A1C4' />,
    'MONGODB': <SiMongodb className={props.classes} size={props.size} color='#66b16a' />,
    'DYNAMODB': <SiAmazondynamodb className={props.classes} size={props.size} color='#4053D6' />,
    'JDBC': <BsBraces className={props.classes} size={props.size} color='#000' />,
    'EC2': <BsFillCloudFill className={props.classes} size={props.size} color='#5C6473' />,
    'STREAM': <FaExchangeAlt className={props.classes} size={props.size} color='#0077FF' />,
    'SFTP': <FaFolderOpen className={props.classes} size={props.size} color='#fdda86' />,
    'AZURE_BLOB': <SiMicrosoftazure className={props.classes} size={props.size} color='#0078d4' />,
    'SHAREPOINT': <SiMicrosoftsharepoint className={props.classes} size={props.size} color='#036C70' />,
    'LOCAL': <FaFolder className={props.classes} size={props.size} color='#6c757d' />,
    'FILE': <FaFolder className={props.classes} size={props.size} color='#6c757d' />,
  };
  const icon = engineIcons[engineKey(props.engine)] || engineIcons['JDBC'];
  return icon;
}



const getTablesFromMetaHits = (pagedMetaData) => {
  let result = [];

  try {
    result = pagedMetaData.map(hit => {
      if (hit) {
        return hit.table;
      }
    })
  } catch(e) {
    console.log(e);
    throw e;
  }

  return result;
}

const getTablesFromEntityTerms = (entityData) => {
  let tables = [];

  try {
    const tableBuckets = entityData && entityData.aggregations && entityData.aggregations.tables && entityData.aggregations.tables.buckets || [];
    // console.log('TableMap',tableBuckets)
    tables = tableBuckets.map(bucket => bucket.key);
  } catch(e) {
    console.log(e);
    throw e;
  }

  return tables;
}

const getOverviewFromTableEntities = (tableSampledRowCountsMap, tableColumnEntityMap) => {
  // console.log('getOverviewFromTableEntities', tableSampledRowCountsMap, tableColumnEntityMap);
  
  const result = {
    LOW_INCIDENCE_FINDINGS: {}
  };
  const lowConfidence = result.LOW_INCIDENCE_FINDINGS;

  for (const table in tableColumnEntityMap) {
    tableColumnEntityMap[table].forEach(column => {
      const findings = column.findings;
      findings.forEach(findingData => {
        const entity = findingData.entity;
        const count = findingData.count;
        let sampledQtyTable = tableSampledRowCountsMap[table] ? tableSampledRowCountsMap[table] : {}
        const sampledRowQty = sampledQtyTable.table_sampled_row_qty ? sampledQtyTable.table_sampled_row_qty :  0;

        const findingsPercent = Math.floor((count / sampledRowQty) * 100);
        // console.log('findingsPercent', table, entity, count, sampledRowQty, findingsPercent);
        // if less than 5%, put as low confidence finding
        if (findingsPercent <= LOW_CONFIDENCE_THRESHOLD) {
            // console.log('getOverviewFromTableEntities', table, column, entity, count, findingsPercent);
            lowConfidence[entity] = lowConfidence[entity] ? (
              lowConfidence[entity] + count
            ) : (
              count
            );
        } else {
          result[entity] = result[entity] ? (
            result[entity] + count
          ) : (
            count
          );
        }
      })
    })
  }

  return result;
}

const getTableColumnEntityMap = (tableEntities) => {
  const result = {};
  const tableBuckets = tableEntities.aggregations.tables.buckets;

  tableBuckets.forEach(tb => {
    const table = tb.key;
    result[table] = [];

    const fieldBuckets = tb.nested_aggs.fields.buckets;
    fieldBuckets.forEach(fb => {
      const item = { col: fb.key, findings: [] };
      const entityBuckets = fb.entities.buckets;

      entityBuckets.forEach(eb => {
        const entity = eb.key;
        const count = eb.count.value;
        item.findings.push({
          entity: entity,
          count: count
        });
      });

      result[table].push(item);
    });
  });

  return result;
}

// Plural item noun for a source ('tables' | 'files' | 'collections'). Thin wrapper over the
// canonical resolver in Utils/nomenclature — use getSourceNomenclature directly when you also
// need the singular or container nouns.
const getDataLabel = (engine, datasourceType) => getSourceNomenclature(engine, datasourceType).item;
// Check error obj for license issues.
// Matches the 403 error shape emitted by LicenseEntitlementFilter in the API:
// { error: "LICENSE_REQUIRED" | "LICENSE_EXPIRED", feature, message }
const checkForLicenseIssue = (e) => {
  const response = e?.response || {};
  const data = response.data || {};
  const statusCode = response.status;

  if (statusCode === 403 && (data.error === 'LICENSE_REQUIRED' || data.error === 'LICENSE_EXPIRED')) {
    return data;
  }
  // Legacy shape for backward compatibility during rollover.
  if (statusCode === 401 && (data.status === 'EXPIRED' || data.status === 'NONE')) {
    return data;
  }
  return null;
}

// const getErrorMessage = (e) => {
//   const response = e && e.response;
//   const data = response && response.data;
//   if (data) {
//     return data;
//   } else {
//     return e;
//   }
// }

const getSearchesInProgress = (searchStatuses) => {
  const result = {};
  /** Job finished (or failed/stopped): do not treat as in-progress even if row counters lag (e.g. 629/633 → 99%). */
  const terminalStatuses = new Set([
    'COMPLETED',
    'SUCCEEDED',
    'COMPLETED_WITH_ERRORS',
    'FAILED',
    'STOPPED',
    'STOPPING',
    'TIMEOUT',
    'ERROR'
  ]);
  searchStatuses.forEach(status => {
    const searchid = status.searchid;
    const state = status.state;
    const glueStatus = status.glue_status;
    if ((glueStatus && terminalStatuses.has(glueStatus)) || (state && terminalStatuses.has(state))) {
      return;
    }
    const totalRows = status.total_rows;
    const totalProcessed = getRowsProcessed(status);
    const lastUpdated = status.last_updated;
    const timeSinceLastUpdated = Date.now() - lastUpdated;
    const isInProgress =
      (timeSinceLastUpdated <= 300000 && totalProcessed < totalRows) ||
      glueStatus === 'RUNNING' ||
      glueStatus === 'QUEUED';

    if (isInProgress) result[searchid] = status;
  });

  return result;
}

const getConnectionsInProgress = (searchStatuses) => {
  const result = {};
  // console.log('getConnectionsInProgress', searchStatuses);

  searchStatuses.forEach(status => {
    const connection = status.connection;
    const glueStatus = status.glue_status;
    const processed = status.total_processed;
    const rows = status.total_rows;
    const isInProgress = (glueStatus === 'RUNNING') || (glueStatus === 'QUEUED');


    // console.log('DEBUG: getConnectionsInProgress', status, glueStatus, processed, rows, isInProgress);
    if (connection && isInProgress) {
      result[connection] = true;
    }
    if (processed && rows && processed >= rows) {
      result[connection] = false;
    }

  });

  return result;
}

// get smallest qty of rows processed
const getRowsProcessed = (status = {}) => {
  let result = 0;
  const processedRows = [];

  for (const prop in status) {
    if (prop.includes('_processed')) processedRows.push(status[prop]);
  }
  if (processedRows.length) result = Math.min(...processedRows);
  return result;
}

const getSearchState = (status = {}) => {
  if (status.job_completed) return 'SUCCEEDED';
  if (status.child_searchid_count > 1) return;
  
  const processed = getRowsProcessed(status);
  const totalRows = status.total_rows;
  const glueStatus = status.glue_status;

  // Check if job is hanging or was stopped outside of C2
  const lastUpdated = status.last_updated || status.creationTime;
  const now = Date.now();
  const timeSinceLastUpdated = now - lastUpdated;

  if (glueStatus && glueStatus !== 'SUCCEEDED') return glueStatus;
  // console.log('getSearchState', status, processed, totalRows);
  if (processed < totalRows) {
    if (timeSinceLastUpdated > 300000) return 'SUCCEEDED';
  } else if (processed >= totalRows) {
    return 'SUCCEEDED';
  }

  // console.log('DEBUG: getSearchState', status, processed, totalRows, timeSinceLastUpdated);

  if (processed >= totalRows || timeSinceLastUpdated > 300000) {
    return 'SUCCEEDED';
  }
  return 'RUNNING';
}

// Prefer the server's structured reason (ApiError.body.{error,message,…}) over the
// generic "POST <url> failed: 400" string so import / save / test flows tell the user
// *why* a request was rejected (e.g. "RELATIONAL config.password is required") instead
// of just the status code. Falls back through the legacy `e.response.data.message`
// shape and then `e.message` for non-ApiError throwables.
const getErrorMessage = (e) => {
  if (!e) return e;
  const body = e.body;
  if (body && typeof body === 'object') {
    if (typeof body.error === 'string' && body.error) return body.error;
    if (typeof body.message === 'string' && body.message) return body.message;
    if (typeof body.detail === 'string' && body.detail) return body.detail;
  }
  if (typeof body === 'string' && body) return body;
  if (e.response && e.response.data && e.response.data.message) return e.response.data.message;
  // No structured reason from the server: map bare auth status codes to a human message
  // rather than leaking the raw "GET /api/... failed: 403 Forbidden" ApiError string.
  if (e.status === 403) return 'You are not authorized to perform this action.';
  if (e.status === 401) return 'Your session has expired. Please sign in again.';
  return e.message || e;
};

const getErrorHandler = (hookFn) => (e) => {
  console.log(e);
  hookFn(getErrorMessage(e))
};

const getColorArr = (length) => {
  return Array.from({ length }, (_, i) => chartSeriesColor(i));
}

const EngineColumn = ({ engine }) => {
  return (
    <div>
      <div className='d-flex align-items-center'>
        { engine ? (
          <span>
            <span className='icon-span mr-1'><EngineIcon engine={engine} /></span>
            <span className='text-span'>{engineLabels[engine]}</span>
          </span>
        ) : (
          <span>-</span>
        )}
      </div>
    </div>
  )
}

const TextFilterComponent = ({ column, columnDef, onFilterChanged, filterRef }) => {
  const [value, setValue] = useState(filterRef.current);

  useEffect(() => {
    onFilterChanged(columnDef.tableData.id, value);
    filterRef.current = value;
  }, [value]);

  return (
    <Form.Control
      data-testid={`filter-column-${String(column).toLowerCase().replace(/\s+/g, '-')}`}
      className='table-text-filter mt-1 mb-1'
      value={value}
      type='text'
      size='sm'
      placeholder={`Filter by ${column}`}
      onChange={(e) => setValue(e.target.value)}
    />
  )
}

const getHostToConnectionsMap = (connections) => {
  const result = {};

  connections.forEach(c => {
    const host = c.host;
    const name = c.name;
    result[host] = name;
  });
  
  return result;
}

const transformTableEntityFields = (tableFields) => {
  const result = {};
  //console.log('transformTableEntityFields', tableFields);

  const buckets = tableFields.aggregations.tables.buckets;
  buckets.forEach(b => {
    const hit = b.table_hits.hits.hits[0];
    const source = hit && hit._source;
    const table = source.table;
    const sampledRowQty = source.table_sampled_row_qty  ? source.table_sampled_row_qty : 0 ;
    result[table] = { table_sampled_row_qty: sampledRowQty };
  });
  // console.log('transformTableEntityFields result', result);
  return result;
}

const getEntityToColumnIncidenceMap = (tableSampledRowCountsMap, tableColumnEntityMap) => {
  // console.log('getEntityToColumnIncidenceMap', tableSampledRowCountsMap, tableColumnEntityMap);
  const result = {};

  for (const table in tableColumnEntityMap) {
    let sampleQty =  tableSampledRowCountsMap[table] ? tableSampledRowCountsMap[table] : {};
    const sampledRowCount = sampleQty.table_sampled_row_qty ? sampleQty.table_sampled_row_qty  : 0 ;
    result[table] = { LOW_INCIDENCE_FINDINGS: { lowIncidenceColumns: [] } };
    const columns = tableColumnEntityMap[table];

    columns.forEach(col => {
      const findings = col.findings;

      findings.forEach(finding => {
        const entity = finding.entity;

        if (!result[table][entity]) {
          result[table][entity] = {
            lowIncidenceColumns: [],
            highIncidenceColumns: []
          };
        }

        const count = finding.count;
        const percent = Math.floor((count / sampledRowCount) * 100);

        if (percent <= LOW_CONFIDENCE_THRESHOLD) {
          result[table][entity].lowIncidenceColumns.push(finding)
          result[table].LOW_INCIDENCE_FINDINGS.lowIncidenceColumns.push(finding);
        } else {
          result[table][entity].highIncidenceColumns.push(finding);
        }
      })
    })
  }
  // console.log('getEntityToColumnIncidenceMap result', result);
  return result;
}

const getRulesMap = (rulesList) => {
  const result = {};

  rulesList.forEach(rules => {
    const name = rules.name || rules.id;
    result[name] = rules;
  });

  return result;
}

export * from './Labeling'

export {
  commafy,
  checkForLicenseIssue,
  EngineIcon,
  EngineColumn,
  engineKey,
  engineLabels,
  enginePorts,
  normalizeEngineToConnectionType,
  flattenObj,
  getConnectionsInProgress,
  getColorArr,
  getDataLabel,
  getDisplayId,
  getDisplayDateNumeric,
  getErrorMessage,
  getErrorHandler,
  getEntityTables,
  getEntityTablesValue,
  getEntityToColumnIncidenceMap,
  getHostToConnectionsMap,
  getTablesFromMetaHits,
  getTablesFromEntityTerms,
  getTableColumnEntityMap,
  getSearchesInProgress,
  getSearchState,
  getGlueTargetData,
  getFirstKeyValue,
  getMetaValue,
  getMonth,
  getOverviewFromTableEntities,
  getRowsProcessed,
  getTime,
  getPieVisData,
  getSourceColor,
  getSourceLabel,
  getReportData,
  getRulesMap,
  getExportData,
  exportData,
  PieChartLegend,
  TextFilterComponent,
  transform,
  transformExportData,
  transformEsData,
  truncateCenter,
  transformOverviewData,
  transformTableEntityFields,
  getDisplayDate,
  getCompactDate,
  getDisplayTime,
  getRiskScore,
  RISK_LEVELS,
  DataSources,
  SecuredIcon,
  getDiscoverResultsJson
};
